import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { ChurchIcon, RouteIcon, DollarSignIcon, UsersIcon, MedalIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon, ShieldCheckIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
const ccuEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'No High School Diploma',
  value: '2'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}];
const ccuStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const degreeLevels = [{
  label: 'Associate',
  value: 'associate'
}, {
  label: "Bachelor's",
  value: 'bachelors'
}, {
  label: "Master's",
  value: 'masters'
}];
interface CcuProgram {
  shortTitle: string;
  longTitle: string;
  degreeLevel: string;
  areaOfStudy: string;
  mProgram: string;
}
const ccuPrograms: CcuProgram[] = [
// Associate
{
  shortTitle: 'A.S. - Project Management',
  longTitle: 'Associate of Science (A.S.) in Project Management',
  degreeLevel: 'associate',
  areaOfStudy: 'business',
  mProgram: '1531'
},
// Bachelor's
{
  shortTitle: 'B.A. - Biblical Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Biblical Studies',
  degreeLevel: 'bachelors',
  areaOfStudy: 'religious-studies',
  mProgram: '631'
}, {
  shortTitle: 'B.A. - Christian Ministry',
  longTitle: 'Bachelor of Arts (B.A.) in Christian Ministry',
  degreeLevel: 'bachelors',
  areaOfStudy: 'religious-studies',
  mProgram: '643'
}, {
  shortTitle: 'B.A. - Communication Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Communication Studies',
  degreeLevel: 'bachelors',
  areaOfStudy: 'liberal-arts',
  mProgram: '649'
}, {
  shortTitle: 'B.A. - Creative Writing',
  longTitle: 'Bachelor of Arts (B.A.) in Creative Writing',
  degreeLevel: 'bachelors',
  areaOfStudy: 'liberal-arts',
  mProgram: '657'
}, {
  shortTitle: 'B.A. - Early Childhood Education (Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Education (Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '669'
}, {
  shortTitle: 'B.A. - Early Childhood Education (without Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Education (without Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '670'
}, {
  shortTitle: 'B.A. - Elementary Education (Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Elementary Education (Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '676'
}, {
  shortTitle: 'B.A. - Elementary Education (without Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Elementary Education (without Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '677'
}, {
  shortTitle: 'B.A. - Special Education, K-12 Generalist (Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Special Education, K-12 Generalist (Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '710'
}, {
  shortTitle: 'B.A. - Special Education, K-12 Generalist (without Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Special Education, K-12 Generalist (without Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '711'
}, {
  shortTitle: 'B.S. - Accountancy',
  longTitle: 'Bachelor of Science (B.S.) in Accountancy',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '620'
}, {
  shortTitle: 'B.S. - Applied Psychology',
  longTitle: 'Bachelor of Science (B.S.) in Applied Psychology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'psychology',
  mProgram: '704'
}, {
  shortTitle: 'B.S. - Applied Psychology - Biblical Studies',
  longTitle: 'Bachelor of Science (B.S.) in Applied Psychology - Biblical Studies',
  degreeLevel: 'bachelors',
  areaOfStudy: 'psychology',
  mProgram: '705'
}, {
  shortTitle: 'B.S. - Applied Psychology - Clinical Counseling',
  longTitle: 'Bachelor of Science (B.S.) in Applied Psychology - Clinical Counseling',
  degreeLevel: 'bachelors',
  areaOfStudy: 'psychology',
  mProgram: '706'
}, {
  shortTitle: 'B.S. - Applied Psychology - Criminal Justice',
  longTitle: 'Bachelor of Science (B.S.) in Applied Psychology - Criminal Justice',
  degreeLevel: 'bachelors',
  areaOfStudy: 'psychology',
  mProgram: '707'
}, {
  shortTitle: 'B.S. - Business Administration',
  longTitle: 'Bachelor of Science (B.S.) in Business Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '634'
}, {
  shortTitle: 'B.S. - Business - Entrepreneurship',
  longTitle: 'Bachelor of Science (B.S.) in Business - Entrepreneurship',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '635'
}, {
  shortTitle: 'B.S. - Business - International Business',
  longTitle: 'Bachelor of Science (B.S.) in Business - International Business',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '636'
}, {
  shortTitle: 'B.S. - Computer Information Technology',
  longTitle: 'Bachelor of Science (B.S.) in Computer Information Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '651'
}, {
  shortTitle: 'B.S. - Computer Information Technology - Cyber Security',
  longTitle: 'Bachelor of Science (B.S.) in Computer Information Technology - Cyber Security',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '652'
}, {
  shortTitle: 'B.S. - Computer Information Technology - Database Management',
  longTitle: 'Bachelor of Science (B.S.) in Computer Information Technology - Database Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '653'
}, {
  shortTitle: 'B.S. - Computer Information Technology, Data Engineering',
  longTitle: 'Bachelor of Science (B.S.) in Computer Information Technology, Data Engineering',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '719'
}, {
  shortTitle: 'B.S. - Computer Information Technology - Networking',
  longTitle: 'Bachelor of Science (B.S.) in Computer Information Technology - Networking',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '654'
}, {
  shortTitle: 'B.S. - Computer Science',
  longTitle: 'Bachelor of Science (B.S.) in Computer Science',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '656'
}, {
  shortTitle: 'B.S. - Criminal Justice',
  longTitle: 'Bachelor of Science (B.S.) in Criminal Justice',
  degreeLevel: 'bachelors',
  areaOfStudy: 'criminal-justice',
  mProgram: '659'
}, {
  shortTitle: 'B.S. - Cyber Security',
  longTitle: 'Bachelor of Science (B.S.) in Cyber Security',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1767'
}, {
  shortTitle: 'B.S. - Data Analytics',
  longTitle: 'Bachelor of Science (B.S.) in Data Analytics',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '668'
}, {
  shortTitle: 'B.S. - Economics',
  longTitle: 'Bachelor of Science (B.S.) in Economics',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '671'
}, {
  shortTitle: 'B.S. - Health Care Administration',
  longTitle: 'Bachelor of Science (B.S.) in Health Care Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'health-care',
  mProgram: '680'
}, {
  shortTitle: 'B.S. - Human Resource Management',
  longTitle: 'Bachelor of Science (B.S.) in Human Resource Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '682'
}, {
  shortTitle: 'B.S. - Information Systems Management',
  longTitle: 'Bachelor of Science (B.S.) in Information Systems Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '684'
}, {
  shortTitle: 'B.S. - Information Systems Management - Cyber Security',
  longTitle: 'Bachelor of Science (B.S.) in Information Systems Management - Cyber Security',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '686'
}, {
  shortTitle: 'B.S. - Logistics and Supply Chain Management',
  longTitle: 'Bachelor of Science (B.S.) in Logistics and Supply Chain Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '688'
}, {
  shortTitle: 'B.S. - Marketing and Sales',
  longTitle: 'Bachelor of Science (B.S.) in Marketing and Sales',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '690'
}, {
  shortTitle: 'B.S. - Nonprofit Management',
  longTitle: 'Bachelor of Science (B.S.) in Nonprofit Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '692'
}, {
  shortTitle: 'B.S. - Organizational Management in Project Management',
  longTitle: 'Bachelor of Science (B.S.) in Organizational Management in Project Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '699'
},
// Master's
{
  shortTitle: 'M.A. - Biblical Studies',
  longTitle: 'Master of Arts (M.A.) in Biblical Studies',
  degreeLevel: 'masters',
  areaOfStudy: 'religious-studies',
  mProgram: '632'
}, {
  shortTitle: 'M.A. - Clinical Mental Health Counseling - Marriage & Family Therapy',
  longTitle: 'Master of Arts (M.A.) in Clinical Mental Health Counseling - Marriage & Family Therapy',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '646'
}, {
  shortTitle: 'M.A. - Clinical Mental Health Counseling - Online Curriculum',
  longTitle: 'Master of Arts (M.A.) in Clinical Mental Health Counseling - Online Curriculum',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '645'
}, {
  shortTitle: 'M.A. - Clinical Mental Health Counseling - On Site Curriculum',
  longTitle: 'Master of Arts (M.A.) in Clinical Mental Health Counseling - On Site Curriculum',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '644'
}, {
  shortTitle: 'M.A. - Clinical Mental Health Counseling - Substance Use Disorders',
  longTitle: 'Master of Arts (M.A.) in Clinical Mental Health Counseling - Substance Use Disorders',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '647'
}, {
  shortTitle: 'M.A. - Counseling Dual - Mental Health & School',
  longTitle: 'Master of Arts (M.A.) in Counseling Dual - Mental Health & School',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '723'
}, {
  shortTitle: 'M.A. - Curriculum and Instruction',
  longTitle: 'Master of Arts (M.A.) in Curriculum and Instruction',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '665'
}, {
  shortTitle: 'M.A. - Curriculum and Instruction - Alternative Licensing',
  longTitle: 'Master of Arts (M.A.) in Curriculum and Instruction - Alternative Licensing',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '662'
}, {
  shortTitle: 'M.A. - Curriculum and Instruction - Biblical Studies',
  longTitle: 'Master of Arts (M.A.) in Curriculum and Instruction - Biblical Studies',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '663'
}, {
  shortTitle: 'M.A. - Curriculum and Instruction - Culturally and Linguistically Diverse',
  longTitle: 'Master of Arts (M.A.) in Curriculum and Instruction - Culturally and Linguistically Diverse',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '664'
}, {
  shortTitle: 'M.A. - Executive Leadership in Christian Ministry',
  longTitle: 'Master of Arts (M.A.) in Executive Leadership in Christian Ministry',
  degreeLevel: 'masters',
  areaOfStudy: 'religious-studies',
  mProgram: '678'
}, {
  shortTitle: 'M.A. - School Counseling',
  longTitle: 'Master of Arts (M.A.) in School Counseling',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '709'
}, {
  shortTitle: 'M.A. - Theological Studies',
  longTitle: 'Master of Arts (M.A.) in Theological Studies',
  degreeLevel: 'masters',
  areaOfStudy: 'religious-studies',
  mProgram: '716'
}, {
  shortTitle: 'M.B.A. - Business Administration - Advanced Accounting',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Advanced Accounting',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '637'
}, {
  shortTitle: 'M.B.A. - Business Administration - Cyber Security',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Cyber Security',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '638'
}, {
  shortTitle: 'M.B.A. - Business Administration, Economics',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration, Economics',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '718'
}, {
  shortTitle: 'M.B.A. - Business Administration - Enterprise Agility',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Enterprise Agility',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '642'
}, {
  shortTitle: 'M.B.A. - Business Administration - Health Care Administration',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Health Care Administration',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '639'
}, {
  shortTitle: 'M.B.A. - Business Administration - Leadership',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Leadership',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '640'
}, {
  shortTitle: 'M.B.A. - Business Administration - Project Management',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Project Management',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '641'
}, {
  shortTitle: 'M.Ed. - Educational Leadership (Principal Licensure)',
  longTitle: 'Master of Education (M.Ed.) in Educational Leadership (Principal Licensure)',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '673'
}, {
  shortTitle: 'M.Ed. - Educational Leadership (without Licensure)',
  longTitle: 'Master of Education (M.Ed.) in Educational Leadership (without Licensure)',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '674'
}, {
  shortTitle: 'M.Ed. - Education - Teaching English to Speakers of Other Languages',
  longTitle: 'Master of Education (M.Ed.) in Education - Teaching English to Speakers of Other Languages',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '675'
}, {
  shortTitle: 'M.Ed. - Special Education - Emphasis in Alternative Licensing',
  longTitle: 'Master of Education (M.Ed.) in Special Education - Emphasis in Alternative Licensing',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '713'
}, {
  shortTitle: 'M.Ed. - Special Education - Endorsement in Special Education Generalist',
  longTitle: 'Master of Education (M.Ed.) in Special Education - Endorsement in Special Education Generalist',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '714'
}, {
  shortTitle: 'M.Ed. - Special Education (Licensure)',
  longTitle: 'Master of Education (M.Ed.) in Special Education (Licensure)',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '715'
}, {
  shortTitle: 'M.Ed. - Special Education (without Licensure)',
  longTitle: 'Master of Education (M.Ed.) in Special Education (without Licensure)',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '712'
}, {
  shortTitle: 'M.S. - Accounting',
  longTitle: 'Master of Science (M.S.) in Accounting',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '621'
}, {
  shortTitle: 'M.S. - Criminal Justice',
  longTitle: 'Master of Science (M.S.) in Criminal Justice',
  degreeLevel: 'masters',
  areaOfStudy: 'criminal-justice',
  mProgram: '660'
}, {
  shortTitle: 'M.S. - Criminal Justice - Campus, Event, and Organizational Safety',
  longTitle: 'Master of Science (M.S.) in Criminal Justice - Campus, Event, and Organizational Safety',
  degreeLevel: 'masters',
  areaOfStudy: 'criminal-justice',
  mProgram: '661'
}, {
  shortTitle: 'M.S. - Cyber Security',
  longTitle: 'Master of Science (M.S.) in Cyber Security',
  degreeLevel: 'masters',
  areaOfStudy: 'information-technology',
  mProgram: '666'
}, {
  shortTitle: 'M.S. - Human Resource Management',
  longTitle: 'Master of Science (M.S.) in Human Resource Management',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '683'
}, {
  shortTitle: 'M.S. - Master in Organizational Leadership',
  longTitle: 'Master of Science (M.S.) in Master in Organizational Leadership',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '701'
}, {
  shortTitle: 'M.S. - Psychology',
  longTitle: 'Master of Science (M.S.) in Psychology',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '703'
}];
export function CCULandingPage() {
  usePageMeta('Colorado Christian University | Online Degree Programs', 'Explore flexible online degree programs at Colorado Christian University. Accredited Christ-centered programs designed for working adults.', {
    ogImage: 'https://cdn.magicpatterns.com/uploads/3d3gnGrvtCroZLDPHFprwK/CCU_1.webp',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [selectedDegreeLevel, setSelectedDegreeLevel] = useState('');
  const [selectedAreaOfStudy, setSelectedAreaOfStudy] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const areaLabels: Record<string, string> = {
    business: 'Business',
    'criminal-justice': 'Criminal Justice',
    education: 'Education',
    'health-care': 'Health Care',
    'information-technology': 'Information Technology',
    'liberal-arts': 'Liberal Arts',
    psychology: 'Psychology & Counseling',
    'religious-studies': 'Religious Studies & Ministry'
  };
  const allAreaSlugs = Object.keys(areaLabels);
  const getAvailableAreas = () => {
    if (!selectedDegreeLevel || selectedDegreeLevel === 'undecided') {
      return allAreaSlugs;
    }
    const areasWithPrograms = new Set<string>();
    ccuPrograms.forEach(prog => {
      if (prog.degreeLevel === selectedDegreeLevel) {
        areasWithPrograms.add(prog.areaOfStudy);
      }
    });
    return allAreaSlugs.filter(slug => areasWithPrograms.has(slug));
  };
  const getAvailablePrograms = () => {
    if (!selectedAreaOfStudy) return [];
    return ccuPrograms.filter(prog => {
      const areaMatch = prog.areaOfStudy === selectedAreaOfStudy;
      const degreeMatch = !selectedDegreeLevel || prog.degreeLevel === selectedDegreeLevel;
      return areaMatch && degreeMatch;
    });
  };
  const handleDegreeLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLevel = e.target.value;
    setSelectedDegreeLevel(newLevel);
    setSelectedAreaOfStudy('');
    setSelectedProgram('');
  };
  const handleAreaOfStudyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newArea = e.target.value;
    setSelectedAreaOfStudy(newArea);
    setSelectedProgram('');
  };
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProgram = e.target.value;
    setSelectedProgram(newProgram);
    if (newProgram && formStep === 0) {
      setFormStep(1);
    }
  };
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      const currentProgram = ccuPrograms.find(p => p.shortTitle === selectedProgram);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          programTitle: currentProgram?.longTitle || '',
          degreeLevel: selectedDegreeLevel,
          areaOfStudy: selectedAreaOfStudy,
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/uv7w6tl/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'Colorado Christian University',
            logo: 'https://cdn.magicpatterns.com/uploads/3d3gnGrvtCroZLDPHFprwK/CCU_1.webp',
            primaryColor: '#0F3057',
            accentColor: '#D4A943',
            backUrl: '/colleges/colorado-christian-university',
            programTitle: currentProgram?.longTitle || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#D4A943] focus:border-[#D4A943]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#D4A943] focus:border-[#D4A943]'}`;
  const availableAreas = getAvailableAreas();
  const availablePrograms = getAvailablePrograms();
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-544">
          <div data-id="element-545">
            <label htmlFor="degree-level-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-546">
              Degree Level
            </label>
            <div className="relative" data-id="element-547">
              <select id="degree-level-select" value={selectedDegreeLevel} onChange={handleDegreeLevelChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#D4A943] focus:border-[#D4A943] font-medium text-gray-800" data-id="element-548">
                <option value="" data-id="element-549">-- Select Degree Level --</option>
                {degreeLevels.map(level => <option key={level.value} value={level.value} data-id="element-550">
                    {level.label}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-551">
                <ChevronDownIcon className="h-4 w-4" data-id="element-552" />
              </div>
            </div>
          </div>

          <div data-id="element-553">
            <label htmlFor="area-of-study-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-554">
              Area of Study
            </label>
            <div className="relative" data-id="element-555">
              <select id="area-of-study-select" value={selectedAreaOfStudy} onChange={handleAreaOfStudyChange} disabled={!selectedDegreeLevel} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#D4A943] focus:border-[#D4A943] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-556">
                <option value="" data-id="element-557">
                  {selectedDegreeLevel ? '-- Select Area of Study --' : 'Select degree level first'}
                </option>
                {availableAreas.map(slug => <option key={slug} value={slug} data-id="element-558">
                    {areaLabels[slug]}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-559">
                <ChevronDownIcon className="h-4 w-4" data-id="element-560" />
              </div>
            </div>
          </div>

          <div data-id="element-561">
            <label htmlFor="program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-562">
              Program
            </label>
            <div className="relative" data-id="element-563">
              <select id="program-select" value={selectedProgram} onChange={handleProgramChange} disabled={!selectedAreaOfStudy} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#D4A943] focus:border-[#D4A943] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-564">
                <option value="" data-id="element-565">
                  {!selectedAreaOfStudy ? 'Select area of study first' : '-- Select Program --'}
                </option>
                {availablePrograms.map(prog => <option key={prog.shortTitle} value={prog.shortTitle} data-id="element-566">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-567">
                <ChevronDownIcon className="h-4 w-4" data-id="element-568" />
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center pt-2" data-id="element-569">
            Select all three options above to continue
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-570">
          <div data-id="element-571">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-572">
              Selected Program
            </label>
            <div className="relative" data-id="element-573">
              <select id="step1-program" value={selectedProgram} onChange={e => setSelectedProgram(e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-574">
                {availablePrograms.map(prog => <option key={prog.shortTitle} value={prog.shortTitle} data-id="element-575">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-576">
                <ChevronDownIcon className="h-4 w-4" data-id="element-577" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-578" />

          <div data-id="element-579">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-580">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-581">
              <option value="" data-id="element-582">-- Please Select --</option>
              {ccuEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-583">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-584">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-585">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-586">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-587">
              <option value="" data-id="element-588">-- Please Select --</option>
              {ccuStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-589">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-590">{errors.startDate}</p>}
          </div>

          <div data-id="element-591">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-592">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-593">
              <option value="" data-id="element-594">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-595">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-596">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-597">
            <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-598">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-599" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#D4A943] to-[#B8902F] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-600">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-601">
          <div data-id="element-602">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-603">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-604" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-605">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-606">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-607" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-608">
                <div data-id="element-609">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-610">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#D4A943] focus:border-[#D4A943] bg-gray-50" placeholder="City" data-id="element-611" />
                </div>
                <div data-id="element-612">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-613">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#D4A943] focus:border-[#D4A943] bg-gray-50" placeholder="State" data-id="element-614" />
                </div>
              </div>
              <div data-id="element-615">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-616">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#D4A943] focus:border-[#D4A943]'}`} placeholder="123 Main St" data-id="element-617" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-618">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-619">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-620">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-621" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#D4A943] to-[#B8902F] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-622">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-623">
          <div className="grid grid-cols-2 gap-3" data-id="element-624">
            <div data-id="element-625">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-626">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-627" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-628">{errors.firstName}</p>}
            </div>
            <div data-id="element-629">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-630">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-631" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-632">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-633">
            <div data-id="element-634">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-635">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-636" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-637">{errors.email}</p>}
            </div>
            <div data-id="element-638">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-639">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-640" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-641">{errors.phone}</p>}
            </div>
          </div>

          <div className="space-y-2 pt-2" data-id="element-642">
            <label className="flex items-start gap-2.5 cursor-pointer" data-id="element-643">
              <input type="checkbox" checked={formData.tcpaConsent} onChange={e => updateFormData('tcpaConsent', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#D4A943] focus:ring-[#D4A943] cursor-pointer flex-shrink-0" data-id="element-644" />
              <span className="text-[10px] text-gray-500 leading-tight" data-id="element-645">
                By submitting this form, I agree to be called or texted at the
                number listed above by or on behalf of Colorado Christian
                University (CCU) using an automatic or AI telephone system. All
                communication will be informational and commercial regarding
                educational services. Consent is not required as a condition of
                enrollment. CCU may also contact me via email.
              </span>
            </label>
            {errors.tcpaConsent && <p className="text-xs text-red-600" data-id="element-646">{errors.tcpaConsent}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-647">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-648">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-649" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#D4A943] to-[#B8902F] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-650">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-651" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-652">
      {/* Header */}
      <header className="bg-white py-3 px-6 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-653">
        <img src="https://cdn.magicpatterns.com/uploads/3d3gnGrvtCroZLDPHFprwK/CCU_1.webp" alt="Colorado Christian University Logo" className="h-14 sm:h-16 w-auto object-contain" data-id="element-654" />
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0F3057] to-[#1A4A7A] relative overflow-hidden" data-id="element-655">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-656" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 flex flex-col lg:flex-row items-center gap-10 lg:gap-16 relative z-10" data-id="element-657">
          {/* Left Content */}
          <div className="flex-1 text-white max-w-xl" data-id="element-658">
            <p className="text-white font-medium mb-3 text-lg tracking-wide" data-id="element-659">
              Colorado Christian University
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] mb-6 text-white tracking-tight" data-id="element-660">
              {selectedProgram && selectedProgram !== 'undecided' ? (() => {
              const currentProg = ccuPrograms.find(p => p.shortTitle === selectedProgram);
              return currentProg?.longTitle || selectedProgram;
            })() : <>
                  Christ-Centered
                  <br data-id="element-661" />
                  Online Degree Programs
                </>}
            </h1>
            <p className="text-lg text-white/85 max-w-lg leading-relaxed" data-id="element-662">
              Colorado Christian University offers accredited online degree
              programs taught with a Christian worldview. With a flexible online
              format, dedicated support staff, and affordable tuition, we equip
              you for success to make a positive impact in the world.
            </p>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-8 sm:p-10 flex-shrink-0 relative z-10" data-id="element-663">
            <form onSubmit={e => e.preventDefault()} id="ccu-lead-form" data-id="element-664">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-665" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-666" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#0F3057] mb-4" data-id="element-667">
                Take the next step in your education.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-668">
                <div className="bg-gradient-to-r from-[#D4A943] to-[#B8902F] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-669"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-670">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-671">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>
        </div>
      </section>

      {/* Why CCU Section */}
      <section className="bg-gray-50 py-16 border-b border-gray-200" data-id="element-672">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-673">
          <div className="mb-12 text-center" data-id="element-674">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F3057]" data-id="element-675">
              Why CCU?
            </h2>
          </div>
          <div className="space-y-8 max-w-4xl mx-auto" data-id="element-676">
            {/* Card 1 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-677">
              <div className="w-20 h-20 bg-[#0F3057] rounded-full flex items-center justify-center flex-shrink-0 text-white" data-id="element-678">
                <ChurchIcon className="w-10 h-10" data-id="element-679" />
              </div>
              <div className="text-center sm:text-left" data-id="element-680">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-681">
                  Integration of Faith and Learning
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-682">
                  All courses are taught with a Christian worldview. This means
                  you will be equipped for success to make a positive impact in
                  the world.
                </p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-683">
              <div className="w-20 h-20 bg-[#0F3057] rounded-full flex items-center justify-center flex-shrink-0 text-white" data-id="element-684">
                <RouteIcon className="w-10 h-10" data-id="element-685" />
              </div>
              <div className="text-center sm:text-left" data-id="element-686">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-687">
                  Flexible Online Format
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-688">
                  CCU's unique online and course schedule format make it
                  possible for you to juggle life, work, and family all while
                  earning your degree.
                </p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-689">
              <div className="w-20 h-20 bg-[#0F3057] rounded-full flex items-center justify-center flex-shrink-0 text-white" data-id="element-690">
                <DollarSignIcon className="w-10 h-10" data-id="element-691" />
              </div>
              <div className="text-center sm:text-left" data-id="element-692">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-693">
                  Affordable
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-694">
                  With competitive tuition rates, scholarships, and discount
                  opportunities, it's possible to earn your degree without
                  breaking the bank.
                </p>
              </div>
            </div>

            {/* Card 4 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-695">
              <div className="w-20 h-20 bg-[#0F3057] rounded-full flex items-center justify-center flex-shrink-0 text-white" data-id="element-696">
                <UsersIcon className="w-10 h-10" data-id="element-697" />
              </div>
              <div className="text-center sm:text-left" data-id="element-698">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-699">
                  Student Success
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-700">
                  CCU sets you up for success with a dedicated support staff who
                  is invested in making sure that you are successful from the
                  first class, to graduation, and beyond.
                </p>
              </div>
            </div>

            {/* Card 5 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-701">
              <div className="w-20 h-20 bg-[#0F3057] rounded-full flex items-center justify-center flex-shrink-0 text-white" data-id="element-702">
                <MedalIcon className="w-10 h-10" data-id="element-703" />
              </div>
              <div className="text-center sm:text-left" data-id="element-704">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-705">
                  Military Focused
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-706">
                  Dedicated to serving our active and veteran military, CCU has
                  a specific enrollment team with expertise in advising those
                  who are military-affiliated. Whether you're a veteran, active
                  duty, or spouse of either, our enrollment team will ensure you
                  maximize your benefits as you achieve your academic goals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Designed for Adult Students */}
      <section className="py-16 bg-white border-b border-gray-200" data-id="element-707">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-708">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F3057] mb-6" data-id="element-709">
            Programs Designed for Adult Students
          </h2>
          <p className="text-gray-600 leading-relaxed mb-10 max-w-3xl mx-auto" data-id="element-710">
            Our online programs are specifically tailored for adult learners who
            need to balance their education with work and family commitments.
            With flexible scheduling and a supportive community, you can achieve
            your academic goals without putting your life on hold.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center" data-id="element-711">
            <div className="flex flex-col items-center" data-id="element-712">
              <div className="w-16 h-16 bg-[#D4A943]/10 rounded-full flex items-center justify-center mb-4" data-id="element-713">
                <ShieldCheckIcon className="w-8 h-8 text-[#D4A943]" data-id="element-714" />
              </div>
              <h3 className="text-base font-bold text-[#0F3057]" data-id="element-715">
                Accredited Programs
              </h3>
            </div>
            <div className="flex flex-col items-center" data-id="element-716">
              <div className="w-16 h-16 bg-[#D4A943]/10 rounded-full flex items-center justify-center mb-4" data-id="element-717">
                <CheckCircle2Icon className="w-8 h-8 text-[#D4A943]" data-id="element-718" />
              </div>
              <h3 className="text-base font-bold text-[#0F3057]" data-id="element-719">
                100% Online
              </h3>
            </div>
            <div className="flex flex-col items-center" data-id="element-720">
              <div className="w-16 h-16 bg-[#D4A943]/10 rounded-full flex items-center justify-center mb-4" data-id="element-721">
                <UsersIcon className="w-8 h-8 text-[#D4A943]" data-id="element-722" />
              </div>
              <h3 className="text-base font-bold text-[#0F3057]" data-id="element-723">
                Dedicated Support
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0F3057] py-16" data-id="element-724">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-725">
          <div className="w-12 h-1 bg-[#D4A943] mx-auto mb-6" data-id="element-726"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-id="element-727">
            Interested in Learning More About Our{' '}
            <span className="text-[#D4A943]" data-id="element-728">Degrees?</span>
          </h2>
          <div className="mt-8" data-id="element-729">
            <button onClick={scrollToForm} className="inline-block bg-white text-[#0F3057] font-bold py-3.5 px-12 rounded-full hover:shadow-lg hover:bg-gray-100 transition-all text-sm tracking-wide" data-id="element-730">
              Get Started Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F5F5] text-gray-500 py-10 text-xs border-t border-gray-200" data-id="element-731">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3" data-id="element-732">
          <p data-id="element-733">Certain degree programs may not be available in all states.</p>
          <p className="pt-4 border-t border-gray-300 text-gray-400" data-id="element-734">
            Colorado Christian University is accredited by the Higher Learning
            Commission (hlcommission.org), an institutional accreditation agency
            recognized by the U.S. Department of Education.
          </p>
          <p className="text-gray-400" data-id="element-735">
            © {new Date().getFullYear()} Colorado Christian University. All
            rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}